import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Phone, CalendarDays, Clock, MapPin, Sparkles, Wallet } from 'lucide-react';
import { BACKEND_URL } from '../config';
import SEO from './SEO';
import LeadOffer from './site/LeadOffer';
import { SitePage, PageHero, Facts, SectionHead, PackageCard, CardSkeletons, CtaBand } from './site/ui';
import { BRAND, inr, toNumber, waLink } from './site/brand';
import { getDestination } from './site/destinations';
import PeakNote from './site/PeakNote';

const pick = (api, local) => (api !== undefined && api !== null && String(api).trim() !== '' ? api : local);

/**
 * Universal destination page used by every destination route
 * (e.g. /dubai-tour-packages, /kerala-tour-packages).
 * Server content is used when present; curated local content fills any gaps
 * so no destination ever renders an empty hero.
 */
const DestinationTemplate = ({ destinationId }) => {
  const navigate = useNavigate();
  const local = useMemo(() => getDestination(destinationId), [destinationId]);
  const [apiDest, setApiDest] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/tours/${destinationId}`)
      .then((r) => {
        if (!alive) return;
        setApiDest(r.data?.destination || null);
        setPackages(Array.isArray(r.data?.packages) ? r.data.packages.map((p) => ({ ...p, destination: p.destination || destinationId })) : []);  // tax label needs the destination (no TCS on Indian trips)
      })
      .catch(() => { if (alive) { setApiDest(null); setPackages([]); } })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [destinationId]);

  const d = useMemo(() => {
    const a = apiDest || {};
    const name = pick(a.name, local?.name) || (destinationId || '').replace(/-/g, ' ');
    const apiCities = Array.isArray(a.popularCities) && a.popularCities.length ? a.popularCities : null;
    const apiHighlights = Array.isArray(a.highlights) && a.highlights.length && a.highlights.every((h) => h && h.title) ? a.highlights : null;
    return {
      name,
      region: local?.region || 'Holidays',
      tagline: pick(a.tagline, local?.tagline),
      description: pick(a.description, local?.description),
      bestTime: pick(a.bestTime, local?.bestTime),
      idealDuration: pick(a.idealDuration, local?.idealDuration),
      popularCities: apiCities || local?.popularCities || [],
      highlights: apiHighlights || local?.highlights || [],
      // Always use the verified photo when we have one
      heroImage: local?.heroImage || a.heroImage || null,
    };
  }, [apiDest, local, destinationId]);

  const best = (p) => p.bestSeller === true || p.best_seller === 'true' || p.best_seller === true;
  const list = filter === 'bestseller' ? packages.filter(best) : packages;
  const hasBest = packages.some(best);
  const prices = packages.map((p) => toNumber(p.price)).filter(Boolean);
  const from = prices.length ? Math.min(...prices) : null;
  const msg = `Hi VK Tours, I'm interested in a ${d.name} holiday. Please share options.`;
  const slug = (destinationId || '').toLowerCase();

  return (
    <SitePage>
      <SEO
        title={`${d.name} Tour Packages · Best Prices`}
        description={`${d.name} holiday packages by VK Tours — ${(d.description || '').slice(0, 130)}`}
        image={d.heroImage || undefined}
        path={typeof window !== 'undefined' ? window.location.pathname : `/destination/${slug}`}
      />

      <PageHero
        image={d.heroImage}
        imageAlt={d.name}
        crumbs={[{ label: 'Home', to: '/' }, { label: d.region === 'India' ? 'India' : 'International', to: '/tour-packages' }, { label: d.name }]}
        kicker={d.tagline}
        title={`${d.name}`}
        accent="holidays"
        lede={d.description}
      >
        <a className="vk-btn vk-btn--hot" href="#packages">See {packages.length || ''} packages <ArrowRight size={18} /></a>
        <a className="vk-btn vk-btn--ghost" href={BRAND.phoneLink}><Phone size={18} /> Call {BRAND.phone}</a>
        <a className="vk-btn vk-btn--ghost" href={waLink(msg)} target="_blank" rel="noopener noreferrer">Get a custom quote</a>
      </PageHero>

      <Facts items={[
        { value: loading ? '…' : String(packages.length || '—'), label: 'Ready packages' },
        { value: from ? inr(from) : 'Custom', label: 'Starting from' },
        { value: d.bestTime || 'Year round', label: 'Best time to visit' },
        { value: d.idealDuration || 'Flexible', label: 'Ideal trip length' },
      ]} />

      {/* Packages */}
      <section className="vk-section vk-section--sand" id="packages" style={{ scrollMarginTop: 'calc(var(--header-h) + 12px)' }}>
        <div className="vk-wrap">
          <SectionHead kicker="Ready to book" title={`${d.name} packages`}>
            {hasBest && (
              <div className="vk-pills" role="tablist" aria-label="Filter packages">
                <button type="button" role="tab" aria-selected={filter === 'all'} className={`vk-pill ${filter === 'all' ? 'is-active' : ''}`} onClick={() => setFilter('all')}>All</button>
                <button type="button" role="tab" aria-selected={filter === 'bestseller'} className={`vk-pill ${filter === 'bestseller' ? 'is-active' : ''}`} onClick={() => setFilter('bestseller')}>Best sellers</button>
              </div>
            )}
          </SectionHead>

          {loading ? <CardSkeletons count={4} /> : list.length > 0 ? (
            <div className="vk-grid">
              {list.map((p) => (
                <PackageCard key={p.id} pkg={p} placeLabel={d.name} onOpen={() => navigate(`/destination/${slug}/package/${p.id}`)} />
              ))}
            </div>
          ) : (
            <div className="vk-empty">
              <h3>We’ll build your {d.name} trip</h3>
              <p>Tell us your dates, budget and who’s travelling — we’ll send a tailor-made plan.</p>
              <a className="vk-btn vk-btn--hot" href={waLink(msg)} target="_blank" rel="noopener noreferrer">Get a quote on WhatsApp</a>
            </div>
          )}
          <p className="vk-form__note" style={{ marginTop: 18 }}>Prices are per person on twin sharing, before government taxes (5% GST{d.region !== 'India' ? ', plus 2% TCS on overseas packages' : ''}), and may change with dates and availability. Call {BRAND.phone} for group or custom quotes.</p>
          <PeakNote compact />
        </div>
      </section>

      <LeadOffer destination={d.name} pageLabel={`${d.name} tour packages`} />

      {/* About + places */}
      <section className="vk-section">
        <div className="vk-wrap vk-split">
          <div>
            <p className="vk-kicker">Why go</p>
            <h2 className="vk-display vk-h2" style={{ marginBottom: 20 }}>The best of {d.name}</h2>
            <div className="vk-prose"><p>{d.description}</p></div>
            <div className="vk-infogrid" style={{ marginTop: 20 }}>
              <div className="vk-info"><CalendarDays size={22} /><div><b>Best time</b><span>{d.bestTime || 'Year round'}</span></div></div>
              <div className="vk-info"><Clock size={22} /><div><b>Trip length</b><span>{d.idealDuration || 'Flexible'}</span></div></div>
              <div className="vk-info"><Wallet size={22} /><div><b>Budget</b><span>{from ? `Packages from ${inr(from)} per person` : 'Quoted to your dates'}</span></div></div>
              <div className="vk-info"><Sparkles size={22} /><div><b>Tailor-made</b><span>Hotels, flights & pace changed to suit you</span></div></div>
            </div>
          </div>
          {d.popularCities.length > 0 && (
            <div className="vk-panel vk-panel--sand">
              <h3>Places you’ll love</h3>
              <ul className="vk-chips">
                {d.popularCities.map((c) => <li key={c} className="vk-chip"><MapPin size={14} /> {c}</li>)}
              </ul>
            </div>
          )}
        </div>
      </section>

      {/* Highlights */}
      {d.highlights.length > 0 && (
        <section className="vk-section vk-section--ink">
          <div className="vk-wrap">
            <SectionHead light kicker="Don’t miss" title={`${d.name} highlights`} />
            <div className="vk-feats">
              {d.highlights.slice(0, 4).map((h, i) => (
                <div key={h.title} className="vk-feat">
                  <span className="vk-display" style={{ fontSize: 40, color: 'var(--hot)' }}>{String(i + 1).padStart(2, '0')}</span>
                  <h3 style={{ color: '#fff' }}>{h.title}</h3>
                  <p>{h.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}


      <CtaBand message={msg} title={`Ready for ${d.name}?`} accent="We'll plan it end to end." />
    </SitePage>
  );
};

export default DestinationTemplate;
